import { useId, useState, type CSSProperties, type KeyboardEvent, type Ref } from "react";

export interface Suggestion {
  key: string;
  label: string;
  /** Shown on the right, muted (the postcode of a zone, the zone of a street). */
  hint: string;
}

interface Props {
  value: string;
  placeholder: string;
  label: string;
  /** The suggestions for what is typed now. */
  suggestions: readonly Suggestion[];
  /** Something was typed (the list opens on it). */
  onText: (text: string) => void;
  onPick: (suggestion: Suggestion) => void;
  /** The list opens on focus too, and the text is selected (the zone: all zones). */
  openOnFocus?: boolean;
  /** Tab takes the highlighted suggestion too (the zone, as in the prototype). */
  tabPicks?: boolean;
  /** Enter while no list is shown (the street: on to the house number). */
  onEnter?: () => void;
  /** The field is left (also after a suggestion was taken with Enter or Tab). */
  onLeave?: () => void;
  inputRef?: Ref<HTMLInputElement>;
  style?: CSSProperties;
}

/**
 * A text field with suggestions below it (prototype: the zone and street fields of a delivery):
 * arrows move through the list, Enter (or Tab) takes the highlighted one, a click takes any.
 */
export function Suggest({
  value,
  placeholder,
  label,
  suggestions,
  onText,
  onPick,
  openOnFocus = false,
  tabPicks = false,
  onEnter,
  onLeave,
  inputRef,
  style,
}: Props) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listId = useId();
  const shown = open && suggestions.length > 0;
  const current = Math.min(active, suggestions.length - 1);

  const pick = (index: number) => {
    const suggestion = suggestions[index];
    if (!suggestion) return;
    setOpen(false);
    onPick(suggestion);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" && suggestions.length > 0) {
      event.preventDefault();
      setOpen(true);
      setActive(Math.min(current + 1, suggestions.length - 1));
    } else if (event.key === "ArrowUp" && suggestions.length > 0) {
      event.preventDefault();
      setActive(Math.max(current - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (shown) pick(current);
      else onEnter?.();
    } else if (event.key === "Tab" && tabPicks && shown) {
      event.preventDefault();
      pick(current);
    } else if (event.key === "Escape" && shown) {
      event.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className="ac" style={style}>
      <input
        ref={inputRef}
        value={value}
        placeholder={placeholder}
        aria-label={label}
        autoComplete="off"
        role="combobox"
        aria-expanded={shown}
        aria-controls={listId}
        aria-autocomplete="list"
        onChange={(event) => {
          setActive(0);
          setOpen(true);
          onText(event.target.value);
        }}
        onFocus={(event) => {
          if (!openOnFocus) return;
          event.target.select();
          setActive(0);
          setOpen(true);
        }}
        onBlur={() => {
          setOpen(false);
          onLeave?.();
        }}
        onKeyDown={onKeyDown}
      />
      {shown && (
        <ul id={listId} role="listbox" aria-label={label}>
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.key}
              role="option"
              aria-selected={index === current}
              className={index === current ? "on" : undefined}
              onMouseDown={(event) => {
                // keep the focus in the field, as in the prototype
                event.preventDefault();
                pick(index);
              }}
            >
              {suggestion.label}
              <span>{suggestion.hint}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
